import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService } from 'shared-components';
import { UsuariosService } from '../../../services/usuarios.service';
import * as _timezones from 'projects/shared-components/src/lib/assets/timezone.json';
import { Subject } from 'rxjs';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-auditores-new-edit',
    templateUrl: './new-edit.component.html'
})
export class AuditoresNewEditComponent implements OnInit, OnDestroy {

    private readonly destroy$ = new Subject<void>();
    public _auditor;

    public newEditForm: UntypedFormGroup;

    public showError: boolean;
    public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
    public mensajes = new Array<Message>();

    public alertasEmail = [];
    public reimpresionesSnc = [];
    public reimpresionesNum = [];
    public excesosDesignaciones = [];

    public toCloseSpinner = 0;

    public disabledIdioma: Subject<boolean> = new Subject();
    public disabledTimezone: Subject<boolean> = new Subject();

    public showPassword = true;
    public showModal = false;
    public usuarioExistente;

    @Input() auditorId;
    @Input() applicationUserId;

    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _spinnerService: SpinnerService,
        private readonly _dataProvider: DataProvider,
        private readonly _usuariosService: UsuariosService,
        private readonly _sessionContext: SessionContextService
    ) { }

    ngOnInit() {
        this.alertasEmail = this._dataProvider.alertasEmail;
        this.reimpresionesSnc = this._dataProvider.alertasReimpresionSnc;
        this.reimpresionesNum = this._dataProvider.alertasRangoPorcentaje;
        this.excesosDesignaciones = this._dataProvider.alertasRangoPorcentaje;

        this.newEditForm = this._fb.group({
            // des: ['', Validators.required],
            entidadControl: ['', Validators.required],
            permisoEscritura: [false, Validators.required],
            duplicacionDesignaciones: [false, Validators.required],
            alertasEmail: ['', Validators.required],
            envioInmediatoAlerta: [false, Validators.required],
            reimpresionesSnc: ['', Validators.required],
            reimpresionesNum: ['', Validators.required],
            excesosDesignaciones: ['', Validators.required],
            userName: ['', [Validators.required, Validators.pattern('^[A-z0-9._%+-]+@[A-z0-9.-]+\\.[a-z]{2,4}$')]],
            password: ['', Validators.required],
            phoneNumber: [''],
            useTwoFactor: [true],
            idioma: ['', Validators.required],
            msTimeZone: ['', Validators.required],
            direccion: [''],
            nombre: ['', Validators.required],
            apellido: ['', Validators.required],
            activo: [true, Validators.required],
            gestionRoyalties: [false],
            mostrarLicencias: ['']
        });

        if (this.auditorId) {
            this.getAuditor();
        }

        this.newEditForm.get('userName').valueChanges
        .pipe(
            debounceTime(1000),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        )
        .subscribe((userName) => {
            if (!this.newEditForm.get('userName').hasError('pattern') && !this.auditorId && !this.showModal &&
                this.role === 'Administrador' && userName !== '' && userName !== null) {
                document.getElementById('userName').blur();
                this._spinnerService.showSpinner();
                const usuario = {
                    userName,
                    roleName: this.newEditForm.get('permisoEscritura').value === true ? 'Auditor' : 'Supervisor'
                };
                this._usuariosService.checkUsuarioExisteYAsociadoARol(usuario).toPromise().finally(() => {
                    this._spinnerService.hideSpinner();
                }).then((resp) => {
                    if (resp) {
                        if (resp.asociadoARol) {
                            this.showModal = true;
                        } else {
                            this.usuarioExistente = resp;
                            this.showPassword = false;
                            this.newEditForm.get('password').clearValidators();
                            this.newEditForm.get('password').updateValueAndValidity();
                            this.fillInfo();
                        }
                    } else {
                        this.newEditForm.get('nombre').enable();
                        this.newEditForm.get('apellido').enable();
                        this.newEditForm.get('phoneNumber').enable();
                        this.newEditForm.get('direccion').enable();
                        this.newEditForm.get('idioma').enable();
                        this.newEditForm.get('msTimeZone').enable();
                        this.newEditForm.get('useTwoFactor').enable();
                        this.newEditForm.get('activo').enable();

                        this.disabledIdioma.next(false);
                        this.disabledTimezone.next(false);

                        document.getElementById('userName').focus();

                        this.showPassword = true;
                        this.newEditForm.get('password').setValidators([Validators.required]);
                        this.newEditForm.get('password').updateValueAndValidity();
                    }
                });
            } else {
                if(!this.auditorId) {
                    this.newEditForm.get('nombre').enable();
                    this.newEditForm.get('apellido').enable();
                    this.newEditForm.get('phoneNumber').enable();
                    this.newEditForm.get('direccion').enable();
                    this.newEditForm.get('idioma').enable();
                    this.newEditForm.get('msTimeZone').enable();
                    this.newEditForm.get('nombre').reset();
                    this.newEditForm.get('apellido').reset();
                    this.newEditForm.get('phoneNumber').reset();
                    this.newEditForm.get('direccion').reset();
                    this.newEditForm.get('idioma').reset();
                    this.newEditForm.get('msTimeZone').reset();

                    this.disabledIdioma.next(false);
                    this.disabledTimezone.next(false);

                    document.getElementById('userName').focus();

                    this.showPassword = true;
                    this.newEditForm.get('password').setValidators([Validators.required]);
                    this.newEditForm.get('password').updateValueAndValidity();
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get entidades() {
        return this._dataProvider.entidadesControl;
    }

    public get idiomas() {
        return this._dataProvider.idiomas;
    }

    public get timezones() {
        return this._dataProvider.timeZones;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getAuditor() {
        this._spinnerService.showSpinner();
        this._usuariosService.getAuditor(this.auditorId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this._auditor = resp;
            this.newEditForm.patchValue({
                // des: resp.des,
                nombre: resp.applicationUser.nombre,
                apellido: resp.applicationUser.apellido,
                entidadControl: this.entidades.find(x => x.id === resp.entidadControlId),
                permisoEscritura: resp.permisoEscritura,
                duplicacionDesignaciones: resp.duplicacionDesignacionesAutorizada,
                userName: resp.applicationUser.userName,
                idioma: this.idiomas.find(x => x.id === resp.applicationUser.idiomaId),
                msTimeZone: this.timezones.find(x => x.id === resp.applicationUser.msTimeZoneId),
                useTwoFactor: resp.applicationUser.twoFactorEnabled,
                alertasEmail: resp.applicationUserAlerta ?
                    this.alertasEmail.find(x => x.id === resp.applicationUserAlerta.frecuenciaComprobacionEnvioAlertas) : this.alertasEmail[0],
                envioInmediatoAlerta: resp.applicationUserAlerta ? resp.applicationUserAlerta.envioInmediatoAlerta : false,
                reimpresionesSnc: resp.applicationUserAlerta ?
                    this.reimpresionesSnc.find(x => x.id === resp.applicationUserAlerta.numReimpresionMismoSeriado) : this.reimpresionesSnc[0],
                reimpresionesNum: resp.applicationUserAlerta ?
                    this.reimpresionesNum.find(x => x.id === resp.applicationUserAlerta.porcentajeReimpresionExcedida) : this.reimpresionesNum[0],
                excesosDesignaciones: resp.applicationUserAlerta ?
                    this.excesosDesignaciones.find(x => x.id === resp.applicationUserAlerta.porcentajeDesignacionExcedida) : this.excesosDesignaciones[0],
                gestionRoyalties: resp.gestionRoyalties ? true : false,
                mostrarLicencias: resp.mostrarLicencias ? true : false
            });

            if (resp.applicationUser.userName) {
                this.newEditForm.get('userName').disable();
                this.newEditForm.get('userName').clearValidators();
                this.newEditForm.get('userName').updateValueAndValidity();
                this.newEditForm.get('password').disable();
                this.newEditForm.get('password').clearValidators();
                this.newEditForm.get('password').updateValueAndValidity();
                this.newEditForm.get('activo').disable();
                this.newEditForm.get('activo').clearValidators();
                this.newEditForm.get('activo').updateValueAndValidity();
            }
        }, (error) => {
            this.showApiError = true;
            if (error.status === 420) {
                this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
            } else {
                this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            }
        });
    }

    public close() {
        this.refresh.emit(false);
    }

    public save() {
        this.checkForm();
        if (this.newEditForm.valid && !this.auditorId) {
            const auditor = {
                usuarioAuditor: {
                    auditor: {
                        // des: this.newEditForm.get('des').value ? this.newEditForm.get('des').value : '',
                        entidadControlId: this.newEditForm.get('entidadControl').value ? this.newEditForm.get('entidadControl').value.id : null,
                        permisoEscritura: this.newEditForm.get('permisoEscritura').value ? this.newEditForm.get('permisoEscritura').value : false,
                        duplicacionDesignacionesAutorizada: this.newEditForm.get('duplicacionDesignaciones').value ?
                            this.newEditForm.get('duplicacionDesignaciones').value : false,
                        gestionRoyalties: this.newEditForm.get('gestionRoyalties').value ?
                        this.newEditForm.get('gestionRoyalties').value : false,
                        mostrarLicencias: this.newEditForm.get('mostrarLicencias').value ? this.newEditForm.get('mostrarLicencias').value : false
                    },
                    applicationUserAlerta: {
                        frecuenciaComprobacionEnvioAlertas: this.newEditForm.get('alertasEmail').value ?
                            this.newEditForm.get('alertasEmail').value.id : null,
                        envioInmediatoAlerta: this.newEditForm.get('envioInmediatoAlerta').value ? this.newEditForm.get('envioInmediatoAlerta').value : false,
                        numReimpresionMismoSeriado: this.newEditForm.get('reimpresionesSnc').value ? this.newEditForm.get('reimpresionesSnc').value.id : null,
                        porcentajeReimpresionExcedida: this.newEditForm.get('reimpresionesNum').value ? this.newEditForm.get('reimpresionesNum').value.id : null,
                        porcentajeDesignacionExcedida: this.newEditForm.get('excesosDesignaciones').value ? this.newEditForm.get('excesosDesignaciones').value.id : null,
                    },
                    roleName: this.newEditForm.get('permisoEscritura').value ? 'Auditor' : 'Supervisor',
                    applicationUser: {
                        userName: this.newEditForm.get('userName').value ? this.newEditForm.get('userName').value : null,
                        password: this.newEditForm.get('password').value,
                        phoneNumber: this.newEditForm.get('phoneNumber').value ? this.newEditForm.get('phoneNumber').value : '',
                        useTwoFactor: this.newEditForm.get('useTwoFactor').value ? this.newEditForm.get('useTwoFactor').value : false,
                        idiomaId: this.newEditForm.get('idioma').value ? this.newEditForm.get('idioma').value.id : null,
                        msTimeZoneId: this.newEditForm.get('msTimeZone').value ? this.newEditForm.get('msTimeZone').value.id : '',
                        nombre: this.newEditForm.get('nombre').value ? this.newEditForm.get('nombre').value : '',
                        apellido: this.newEditForm.get('apellido').value ? this.newEditForm.get('apellido').value : '',
                        direccion: this.newEditForm.get('direccion').value ? this.newEditForm.get('direccion').value : '',
                        activo: this.newEditForm.get('activo').value ? this.newEditForm.get('activo').value : false,
                    },
                }
            };
            this._spinnerService.showSpinner();
            this._usuariosService.createAuditor(auditor).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                if (resp) {
                    this.refresh.emit(true);
                    this.mensaje.emit('global.success.itemCreated');
                } else {
                    this.showError = true;
                    this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            },
                (error) => {
                    if (error.status === 420) {
                        switch (error.error.ApiErrorCode) {
                            case 1027:
                                this.pushMessage(error.error.Descripcion);
                                break;
                        }
                    }
                });
        } else if (this.newEditForm.valid && this.auditorId) {
            const auditor = {
                auditorId: this.auditorId,
                entidadControlId: this.newEditForm.get('entidadControl').value ? this.newEditForm.get('entidadControl').value.id : null,
                nombre: this.newEditForm.get('nombre').value ? this.newEditForm.get('nombre').value : '',
                apellido: this.newEditForm.get('apellido').value ? this.newEditForm.get('apellido').value : '',
                permisoEscritura: this.newEditForm.get('permisoEscritura').value ? this.newEditForm.get('permisoEscritura').value : false,
                duplicacionDesignacionesAutorizada: this.newEditForm.get('duplicacionDesignaciones').value ?
                    this.newEditForm.get('duplicacionDesignaciones').value : false,
                useTwoFactor: this.newEditForm.get('useTwoFactor').value ? this.newEditForm.get('useTwoFactor').value : false,
                //idiomaId: this.newEditForm.get('idioma').value ? this.newEditForm.get('idioma').value.idiomaId : null,
                msTimeZoneId: this.newEditForm.get('msTimeZone').value ? this.newEditForm.get('msTimeZone').value.id : '',
                gestionRoyalties: this.newEditForm.get('gestionRoyalties').value ? this.newEditForm.get('gestionRoyalties').value : false,
                mostrarLicencias: this.newEditForm.get('mostrarLicencias').value ? this.newEditForm.get('mostrarLicencias').value : false,
                applicationUserAlerta: {
                    userName: this.newEditForm.get('userName')?.value ? this.newEditForm.get('userName')?.value : '',
                    frecuenciaComprobacionEnvioAlertas: this.newEditForm.get('alertasEmail')?.value ?
                            this.newEditForm.get('alertasEmail')?.value.id : null,
                    envioInmediatoAlerta: this.newEditForm.get('envioInmediatoAlerta')?.value ? this.newEditForm.get('envioInmediatoAlerta')?.value : false,
                    numReimpresionMismoSeriado: this.newEditForm.get('reimpresionesSnc')?.value ? this.newEditForm.get('reimpresionesSnc')?.value.id : null,
                    porcentajeReimpresionExcedida: this.newEditForm.get('reimpresionesNum')?.value ? this.newEditForm.get('reimpresionesNum')?.value.id : null,
                    porcentajeDesignacionExcedida: this.newEditForm.get('excesosDesignaciones')?.value ? this.newEditForm.get('excesosDesignaciones')?.value.id : null,
                }
            };

            this._spinnerService.showSpinner();
            this._usuariosService.updateAuditor(auditor).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                this.refresh.emit(true);
                this.mensaje.emit('global.success.itemUpdated');
            });
        }
    }

    private checkForm() {
        this.showError = this.newEditForm.invalid;
        if (this.showError) {
            this.mensajes.push({ class: 'error', message: 'global.error.required' });
        }
    }

    public pushMessage(mensaje) {
        this.showError = true;
        this.mensajes.push({ class: 'error', message: mensaje });
    }

    public fillInfo() {
        this.newEditForm.patchValue({
            nombre: this.usuarioExistente.nombre,
            apellido: this.usuarioExistente.apellido,
            phoneNumber: this.usuarioExistente.phoneNumber,
            direccion: this.usuarioExistente.direccion,
            idioma: this.idiomas.find(x => x.id === this.usuarioExistente.idiomaId),
            msTimeZone: this.timezones.find(x => x.id === this.usuarioExistente.msTimeZoneId),
            useTwoFactor: this.usuarioExistente.twoFactorEnabled,
            activo: this.usuarioExistente.activo
        });

        this.newEditForm.get('nombre').disable();
        this.newEditForm.get('apellido').disable();
        this.newEditForm.get('idioma').disable();
        this.newEditForm.get('msTimeZone').disable();
        this.newEditForm.get('direccion').disable();
        this.newEditForm.get('phoneNumber').disable();
        this.newEditForm.get('useTwoFactor').disable();
        this.newEditForm.get('activo').disable();

        this.disabledIdioma.next(true);
        this.disabledTimezone.next(true);

        this.showPassword = false;
        this.newEditForm.get('password').clearValidators();
        this.newEditForm.get('password').updateValueAndValidity();
    }

    public hideModal() {
        this.showModal = false;

        this.showPassword = true;
        this.newEditForm.get('password').setValidators([Validators.required]);
        this.newEditForm.get('password').updateValueAndValidity();

        this.newEditForm.get('nombre').enable();
        this.newEditForm.get('apellido').enable();
        this.newEditForm.get('phoneNumber').enable();
        this.newEditForm.get('direccion').enable();
        this.newEditForm.get('idioma').enable();
        this.newEditForm.get('msTimeZone').enable();

        this.disabledIdioma.next(false);
        this.disabledTimezone.next(false);
    }
}
